import { useState } from "react";
import { Link, useLocation } from "@tanstack/react-router";
import { srcSetFor } from "@/lib/responsive";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import logo from "@/assets/logo.png";

const navLinks = [
  { label: "Home", path: "/" },
  { label: "Services", path: "/services" },
  { label: "Our Work", path: "/our-work" },
  { label: "About", path: "/about" },
  { label: "FAQ", path: "/faq" },
  { label: "Contact", path: "/contact" },
];

const FloatingNav = ({ sticky = true }: { sticky?: boolean }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`${sticky ? "fixed" : "absolute"} top-5 right-5 z-50 w-12 h-12 rounded-full bg-card shadow-lg border border-border flex items-center justify-center hover:scale-105 transition-transform md:hidden`}
        aria-label="Open navigation"
      >
        <Menu className="w-6 h-6 text-foreground" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] bg-card/95 backdrop-blur-sm flex flex-col items-center justify-center"
          >
            <button
              onClick={() => setOpen(false)}
              className="absolute top-5 right-5 w-12 h-12 rounded-full bg-muted flex items-center justify-center hover:scale-105 transition-transform"
              aria-label="Close navigation"
            >
              <X className="w-6 h-6 text-foreground" />
            </button>

            <img src={logo} srcSet={srcSetFor(logo)} sizes="300px" alt="JTB Floors" width={1536} height={1024} decoding="async" className="h-16 w-auto mb-10" />

            <nav className="flex flex-col items-center gap-2" aria-label="Main navigation">
              {navLinks.map((link, i) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06 }}
                >
                  <Link
                    to={link.path}
                    onClick={() => setOpen(false)}
                    className={`text-2xl md:text-3xl font-heading font-bold px-6 py-3 rounded-lg transition-colors ${
                      location.pathname === link.path
                        ? "text-primary"
                        : "text-foreground/70 hover:text-primary"
                    }`}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
            </nav>

            <div className="flex flex-col sm:flex-row items-center gap-3 mt-10">
              <Button variant="outline" asChild className="gap-2 border-foreground/30 text-foreground bg-card">
                <Link to="/contact" hash="form" onClick={() => setOpen(false)}>Request Estimate</Link>
              </Button>
              <Button asChild className="btn-shine gap-2">
                <a href="tel:17739342004">
                  <Phone className="w-4 h-4" /> Call (773) 934-2004
                </a>
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingNav;
